<template>
  <v-dialog v-model="internalOpen" max-width="500">
    <div class="ai-visibility-pause-dialog">
      <div class="ai-visibility-pause-dialog__head">
        <h2>Pause this report's schedule?</h2>
        <button
          type="button"
          class="ai-visibility-pause-dialog__close"
          aria-label="Close pause confirmation"
          :disabled="saving"
          @click="close"
        >
          <span class="material-icons">close</span>
        </button>
      </div>

      <div class="ai-visibility-pause-dialog__body">
        <p>
          While paused, this report <strong>won't refresh automatically and
          won't spend any credits.</strong> Your latest results and history
          stay available to view.
        </p>

        <ul class="ai-visibility-pause-dialog__list">
          <li>
            <span class="material-icons-outlined">check</span>
            No weekly / monthly charges while paused
          </li>
          <li>
            <span class="material-icons-outlined">check</span>
            You can still “Run now” manually anytime
          </li>
          <li>
            <span class="material-icons-outlined">check</span>
            Resume with one click — the schedule picks back up
          </li>
        </ul>

        <p>
         Only one report can be active at a time, so resuming a paused report later
         will pause whichever report is active then.
        </p>
      </div>

      <div class="ai-visibility-pause-dialog__actions">
        <button
          type="button"
          class="ai-visibility-pause-dialog__cancel"
          :disabled="saving"
          @click="close"
        >
          Cancel
        </button>
        <button
          type="button"
          class="ai-visibility-pause-dialog__confirm"
          :disabled="saving || !tracker"
          @click="confirm"
        >
          {{ saving ? 'Pausing...' : 'Pause schedule' }}
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
export default {
  name: 'PauseTrackerDialog',
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    tracker: {
      type: Object,
      default: null,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    internalOpen: {
      get() {
        return this.open
      },
      set(value) {
        if (!value) {
          this.close()
        }
      },
    },
  },
  methods: {
    close() {
      if (this.saving) {
        return
      }
      this.$emit('close')
    },
    confirm() {
      if (this.saving || !this.tracker) {
        return
      }
      this.$emit('confirm', this.tracker)
    },
  },
}
</script>

<style scoped lang="scss">

.ai-visibility-pause-dialog {
  overflow: hidden;
  border-radius: 16px;
  background: var(--v-cardBg-base);
  box-shadow: 0 30px 80px rgba(1, 35, 35, 0.3);
  color: var(--v-mainColor-base);
}

.ai-visibility-pause-dialog__head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 24px 28px 0;
}

.ai-visibility-pause-dialog__head h2 {
  flex: 1;
  color: var(--v-mainColor-base);
  font-size: 21px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0;
}

.ai-visibility-pause-dialog__close {
  display: inline-flex;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--v-darkGrayColor-base);
  cursor: pointer;
  padding: 0;
}

.ai-visibility-pause-dialog__close:hover,
.ai-visibility-pause-dialog__close:focus {
  background: var(--v-gray7Color-base);
  color: var(--v-mainColor-base);
  outline: none;
}

.ai-visibility-pause-dialog__close:disabled {
  cursor: default;
  opacity: 0.55;
}

.ai-visibility-pause-dialog__close span {
  font-size: 20px;
}

.ai-visibility-pause-dialog__body {
  padding: 18px 28px 4px;
}

.ai-visibility-pause-dialog__body p {
  color: var(--v-darkGrayColor-base);
  font-size: 14px;
  line-height: 1.55;
  margin: 0 0 14px;
}

.ai-visibility-pause-dialog__body strong {
  color: var(--v-mainColor-base);
  font-weight: 700;
}

.ai-visibility-pause-dialog__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid var(--v-gray13Color-base);
  border-radius: 10px;
  background: var(--v-gray7Color-base);
  list-style: none;
  margin: 0 0 6px;
  padding: 12px 14px;
}

.ai-visibility-pause-dialog__list li {
  display: flex;
  align-items: center;
  gap: 9px;
  color: var(--v-darkGrayColor-base);
  font-size: 13px;
  line-height: 1.35;
}

.ai-visibility-pause-dialog__list span {
  flex: 0 0 auto;
  color: var(--v-gray12Color-base);
  font-size: 16px;
}

.ai-visibility-pause-dialog__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 28px 24px;
}

.ai-visibility-pause-dialog__cancel,
.ai-visibility-pause-dialog__confirm {
  min-height: 38px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  padding: 0 16px;
}

.ai-visibility-pause-dialog__cancel {
  border: 0;
  background: transparent;
  color: var(--v-darkGrayColor-base);
}

.ai-visibility-pause-dialog__cancel:hover,
.ai-visibility-pause-dialog__cancel:focus {
  background: var(--v-gray7Color-base);
  color: var(--v-mainColor-base);
  outline: none;
}

.ai-visibility-pause-dialog__confirm {
  margin-left: auto;
  border: 1px solid var(--v-gray13Color-base);
  background: var(--v-cardBg-base);
  color: var(--v-mainColor-base);
}

.ai-visibility-pause-dialog__confirm:hover,
.ai-visibility-pause-dialog__confirm:focus {
  background: var(--v-gray7Color-base);
  outline: none;
}

.ai-visibility-pause-dialog__cancel:disabled,
.ai-visibility-pause-dialog__confirm:disabled {
  cursor: default;
  opacity: 0.55;
}

</style>
